import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { api } from '../../lib/api';
import { subscribeToStation } from '../../lib/realtime';

/**
 * How long one lasts when the server has not said.
 *
 * The socket carries the real number, which is the one that matters - this is
 * only for the local copy of your own, and the two agree today.
 */
const REACTION_SECONDS = 6;

/**
 * Everything a person in a crowd can be seen doing.
 *
 * One table, because the glyph and the words are two views of the same thing
 * and keeping them apart is how a reaction ends up with a button and no
 * sentence - or, worse, a chat line reading "@cairn undefined".
 *
 * The verbs are past tense and short: these land in chat as stage directions
 * next to things people actually typed, so "cairn clapped" is a note about the
 * room where "cairn says clap" would be a message they never sent.
 *
 * `mosh` and `sway` are in here and not in the buttons below. `ReactAction`
 * accepts them, so a client that sends one has to render as something.
 */
export const REACTION_META: Record<string, { glyph: string; verb: string }> = {
  clap: { glyph: '👏', verb: 'clapped' },
  dance: { glyph: '🕺', verb: 'started dancing' },
  wave: { glyph: '👋', verb: 'waved' },
  hooray: { glyph: '🙌', verb: 'cheered' },
  pump: { glyph: '✊', verb: 'threw a fist up' },
  lighter: { glyph: '🔥', verb: 'held a lighter up' },
  bang: { glyph: '🤘', verb: 'is headbanging' },
  mosh: { glyph: '🏃', verb: 'jumped' },
  sway: { glyph: '🌊', verb: 'is swaying' },
};

/** The ones offered as buttons, in the order they are offered. */
const OFFERED = ['clap', 'dance', 'wave', 'hooray', 'pump', 'lighter', 'bang'];

/** The button list: a key and the glyph on it. Derived, so it cannot drift. */
export const REACTIONS: [string, string][] = OFFERED.map((key) => [
  key,
  REACTION_META[key]?.glyph ?? '',
]);

/**
 * Who is reacting, by username and with what - and how to react yourself.
 *
 * Also refreshes the crowd when somebody walks in or out, because the two
 * arrive on the same channel and every caller wants both.
 *
 * ## Your own is applied locally, and that is a correction
 *
 * It used to be left entirely to the socket, on the argument that the person
 * who pressed the button should see exactly what the room sees and a local
 * shortcut would be the one copy that could differ. The argument is right and
 * the conclusion was wrong: the subscription is established asynchronously, so
 * pressing a reaction in the second after walking into a room posts it, shows
 * it to everybody else, and drops your own echo - which is the one copy
 * differing, in the direction where you see less than the room.
 *
 * Applying it on a successful post cannot disagree with the socket: it is the
 * same key and the same value, and a refused post applies nothing. Everybody
 * else still learns about it the only way they can.
 */
export function useRoomReactions(
  stationId: number,
  username: string,
  /** The viewer, so their own reaction can be shown without waiting. */
  viewer?: string | null,
) {
  const queryClient = useQueryClient();
  const [reacting, setReacting] = useState<Record<string, string>>({});

  /*
   * Held across renders because two things queue onto it - the socket and the
   * local echo below - and both have to be cleared when the room is left.
   */
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const queued = timers.current;

    const stop = subscribeToStation(stationId, (event, payload) => {
      /*
       * Somebody arrived or left. The event carries nothing on purpose - the
       * crowd is a list with a visibility rule on it, so this asks rather than
       * being told. Until it existed the crowd was a twenty-second poll and
       * nothing else, so you could stand in a room for twenty seconds before
       * appearing in it.
       */
      if (event === 'station.crowd') {
        void queryClient.invalidateQueries({ queryKey: ['crowd', username] });

        return;
      }

      if (event !== 'station.react') return;

      const { username: who, reaction, seconds } = (payload ?? {}) as {
        username?: string;
        reaction?: string;
        seconds?: number;
      };

      if (!who || !reaction) return;

      setReacting((current) => ({ ...current, [who]: reaction }));

      queued.push(
        setTimeout(
          () =>
            setReacting((current) => {
              // Only if they have not started something else since - clearing
              // blindly would cut a second reaction short.
              if (current[who] !== reaction) return current;

              const { [who]: _gone, ...rest } = current;

              return rest;
            }),
          (seconds ?? 6) * 1000,
        ),
      );
    });

    return () => {
      stop?.();
      queued.forEach(clearTimeout);
      queued.length = 0;
    };
  }, [stationId, username, queryClient]);

  const react = useMutation({
    mutationFn: (reaction: string) =>
      api.post(`/users/${encodeURIComponent(username)}/live/react`, { reaction }),
    onSuccess: (_result, reaction) => {
      if (!viewer) return;

      setReacting((current) => ({ ...current, [viewer]: reaction }));

      timers.current.push(
        setTimeout(
          () =>
            setReacting((current) => {
              if (current[viewer] !== reaction) return current;

              const { [viewer]: _gone, ...rest } = current;

              return rest;
            }),
          REACTION_SECONDS * 1000,
        ),
      );
    },
  });

  return { reacting, react };
}
